import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, createApiClient, Link, LinkPayload, User } from './api';
import LinkForm from './components/LinkForm';
import LinkTable from './components/LinkTable';
import QRCodeModal from './components/QRCodeModal';
import AnalyticsModal from './components/AnalyticsModal';
import LanguagePicker from './components/LanguagePicker';
import Pricing, { LegalPage } from './components/Pricing';
import LegalPages from './components/LegalPages';
import CookieBanner from './components/CookieBanner';
import Footer from './components/Footer';
import MoveToFolderModal from './components/MoveToFolderModal';
import CreateFolderModal from './components/CreateFolderModal';

import { getLanguage, languages, Lang, translate, TranslationKey } from './i18n';

const STORAGE_KEYS = {
  authToken: 'shorturl-auth-token',
  lang: 'shorturl-lang',
};

const defaultApiBase = import.meta.env.VITE_API_BASE_URL ?? '/api';
const defaultShortDomain =
  import.meta.env.VITE_SHORT_DOMAIN ??
  (typeof window !== 'undefined' ? window.location.origin : 'https://short.example.com');
const proPriceLabel = '5,99€';


const getAssetBase = () => {
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname;
  if (path === '/app' || path.startsWith('/app/')) {
    return `${window.location.origin}/app`;
  }
  return window.location.origin;
};

const getLocalValue = (key: string, fallback: string) => {
  if (typeof window === 'undefined') return fallback;
  return window.localStorage.getItem(key) ?? fallback;
};

const App = () => {
  const apiBaseUrl = defaultApiBase;
  const shortDomain = defaultShortDomain;
  const [authToken, setAuthToken] = useState(() => getLocalValue(STORAGE_KEYS.authToken, ''));
  const [assetBase] = useState(() => getAssetBase());
  const [lang, setLang] = useState<Lang>(() => {
    const stored = getLocalValue(STORAGE_KEYS.lang, '');
    return getLanguage(stored || 'en');
  });
  const [user, setUser] = useState<User | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [links, setLinks] = useState<Link[]>([]);
  const [folders, setFolders] = useState<import('./api').Folder[]>([]);
  const [loading, setLoading] = useState(true);

  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [quickUrl, setQuickUrl] = useState('');
  const [quickAlias, setQuickAlias] = useState('');
  const [quickNote, setQuickNote] = useState('');
  const [quickCode, setQuickCode] = useState('');
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileFormOpen, setMobileFormOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>('system');

  // Close mobile menu on larger screens or when clicking content
  useEffect(() => {
    const closeMenu = () => setMobileMenuOpen(false);
    window.addEventListener('resize', closeMenu);
    return () => window.removeEventListener('resize', closeMenu);
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.profile-avatar-wrapper')) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [profileOpen]);

  // Theme management
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'light') {
      root.setAttribute('data-theme', 'light');
    } else if (theme === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
  }, [theme]);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState<Link | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);
  const [health, setHealth] = useState<'ok' | 'error'>('error');
  const [billingRefresh, setBillingRefresh] = useState(false);
  const [legalPage, setLegalPage] = useState<LegalPage | null>(null);
  const [qrLink, setQrLink] = useState<Link | null>(null);
  const [analyticsLink, setAnalyticsLink] = useState<Link | null>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [movingLink, setMovingLink] = useState<Link | null>(null);

  const client = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl || '/api', token: authToken || undefined }),
    [apiBaseUrl, authToken]
  );

  const langMeta = useMemo(() => languages.find((entry) => entry.code === lang) ?? languages[0], [lang]);
  const locale = langMeta.locale;
  const dir = langMeta.dir ?? 'ltr';
  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string>) => translate(lang, key, vars),
    [lang]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEYS.lang, lang);
  }, [lang]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const link = document.querySelector("link[rel~='icon']");
    if (link) {
      link.setAttribute('href', '/favicon.svg');
    }
  }, [assetBase]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (authToken) {
      window.localStorage.setItem(STORAGE_KEYS.authToken, authToken);
    } else {
      window.localStorage.removeItem(STORAGE_KEYS.authToken);
    }
  }, [authToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const run = async () => {
      try {
        const ok = await client.health();
        setHealth(ok ? 'ok' : 'error');
      } catch (error) {
        setHealth('error');
      }
    };
    run();
  }, [client]);

  const loadFolders = useCallback(async () => {
    if (!user) {
      setFolders([]);
      return;
    }
    try {
      const list = await client.getFolders();
      setFolders(list);
    } catch (error) {
      console.error('Failed to load folders');
    }
  }, [client, user]);

  const loadLinks = useCallback(
    async (term?: string) => {
      if (!user) {
        setLinks([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const list = await client.list(term && term.length > 0 ? term : undefined);
        setLinks(list);
      } catch (error) {
        // Error toast removed - silent operation
      } finally {
        setLoading(false);
      }
    },
    [client, user, t]
  );

  useEffect(() => {
    if (user) {
      loadFolders();
      loadLinks();
    } else {
      setLoading(false);
    }
  }, [user, loadFolders, loadLinks]);

  useEffect(() => {
    if (user) {
      loadLinks(debouncedSearch);
    }
  }, [debouncedSearch, loadLinks, user]);

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const tokenFromQuery = params.get('token');
    const oauthError = params.get('error');
    const billing = params.get('billing');
    if (tokenFromQuery) {
      setAuthToken(tokenFromQuery);
      params.delete('token');
    }
    if (oauthError) {
      setAuthError('Unable to sign in with Google');
      params.delete('error');
    }
    if (billing) {
      if (billing === 'success') {
        // Success toast removed - silent operation
        setBillingRefresh(true);
      } else if (billing === 'cancel') {
        // Error toast removed - silent operation
      }
      params.delete('billing');
    }
    if (tokenFromQuery || oauthError || billing) {
      const newQuery = params.toString();
      const newUrl = `${window.location.pathname}${newQuery ? `?${newQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    if (!billingRefresh || !authToken) return;
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      attempt += 1;
      try {
        const profile = await client.me();
        if (cancelled) return;
        setUser(profile);
        if (profile.plan === 'pro') {
          // Success toast removed - silent operation
          setBillingRefresh(false);
          return;
        }
      } catch {
        // ignore
      }

      if (!cancelled && attempt < 6) {
        setTimeout(poll, 1200);
      } else if (!cancelled) {
        setBillingRefresh(false);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [authToken, billingRefresh, client]);

  useEffect(() => {
    if (!authToken) {
      setUser(null);
      setLinks([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        const profile = await client.me();
        if (!cancelled) {
          setUser(profile);
        }
      } catch (error) {
        if (!cancelled) {
          setAuthToken('');
          setUser(null);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [authToken, client]);

  const handleCreate = useCallback(async (payload: LinkPayload) => {
    if (!user) return;
    setSubmitting(true);
    try {
      await client.create(payload);
      // Success toast removed - silent operation
      await loadLinks(debouncedSearch);
      const profile = await client.me();
      setUser(profile);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'plan_limit') {
        // Error toast removed - silent operation
      } else {
        // Error toast removed - silent operation
      }
    } finally {
      setSubmitting(false);
    }
  }, [user, client, t, loadLinks, debouncedSearch]);

  const handleQuickShorten = useCallback(async () => {
    if (!quickUrl.trim() || !user) return;
    setQuickSubmitting(true);
    try {
      const payload: LinkPayload = {
        destination: quickUrl.trim(),
        slug: quickAlias.trim() || undefined,
        description: quickNote.trim() || undefined,
        password: quickCode.trim() || undefined,
      };
      await client.create(payload);
      // Success toast removed - silent operation
      setQuickUrl('');
      setQuickAlias('');
      setQuickNote('');
      setQuickCode('');
      await loadLinks(debouncedSearch);
      const profile = await client.me();
      setUser(profile);
    } catch (error) {
      if (error instanceof ApiError && error.code === 'plan_limit') {
        // Error toast removed - silent operation
      } else {
        // Error toast removed - silent operation
      }
    } finally {
      setQuickSubmitting(false);
    }
  }, [quickUrl, quickAlias, quickNote, quickCode, user, client, t, loadLinks, debouncedSearch]);

  const handleUpdate = useCallback(async (payload: LinkPayload) => {
    if (!editing || !user) return;
    setEditSubmitting(true);
    try {
      // Use editing.slug (original) for API URL, pass new slug in payload
      await client.update(editing.slug, payload);
      // Success toast removed - silent operation
      setEditing(null);
      await loadLinks(debouncedSearch);
    } catch (error) {
      // Error toast removed - silent operation
    } finally {
      setEditSubmitting(false);
    }
  }, [user, client, t, loadLinks, debouncedSearch, editing]);

  const handleMoveLink = useCallback(async (folderId: number | null) => {
    if (!movingLink) return;
    const scrollY = window.scrollY;
    try {
      await client.update(movingLink.slug, {
        destination: movingLink.destination,
        folderId,
      });
      await loadLinks(debouncedSearch);
      setMovingLink(null);
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    } catch (e) {
      // Error toast removed - silent operation
    }
  }, [movingLink, client, loadLinks, debouncedSearch]);

  const handleUpdateLinkFolder = useCallback(async (slug: string, folderId: number | null) => {
    // 1. Optimistic Update
    setLinks((prevLinks) =>
      prevLinks.map((l) => (l.slug === slug ? { ...l, folderId } : l))
    );

    try {
      const link = links.find((l) => l.slug === slug);
      if (!link) return;

      const payload: LinkPayload = {
        destination: link.destination,
        description: link.description || undefined,
        folderId,
      };

      await client.update(slug, payload);
      // Removed loadLinks to prevent refresh flicker
    } catch (e) {
      console.error(e);
      // Revert optimism on error could be added here if strict consistency needed
      // For now, silent fail or handle error gracefully
      await loadLinks(debouncedSearch); // Only reload on error to restore state
    }
  }, [links, client, loadLinks, debouncedSearch]);

  const handleDeleteFolder = useCallback(async (folderId: number) => {
    // Optimistic Update
    const previousFolders = [...folders];
    const previousLinks = [...links];

    try {
      // 1. Remove folder from UI immediately
      setFolders(prev => prev.filter(f => f.id !== folderId));

      // 2. Move links to "Uncategorized" (folderId: null) in UI immediately
      setLinks(prev => prev.map(link =>
        link.folderId === folderId ? { ...link, folderId: null } : link
      ));

      // 3. Call API in background
      await client.deleteFolder(folderId);

      // Update folder list silently to ensure sync
      const updatedFolders = await client.getFolders();
      setFolders(updatedFolders);

      // Do NOT call loadLinks() to avoid scroll jump/refresh
    } catch (e) {
      console.error(e);
      // Error toast removed - silent operation
      // Revert on error
      setFolders(previousFolders);
      setLinks(previousLinks);
    }
  }, [folders, links, client]);

  const handleEditFolder = useCallback(async (folderId: number, name: string) => {
    try {
      await client.updateFolder(folderId, name);
      await loadFolders();
    } catch (e) {
      console.error('Error renaming folder', e);
    }
  }, [client, loadFolders]);

  const handleReorderFolders = useCallback(async (order: number[]) => {
    // Optimistic Update: Reorder local state immediately
    setFolders(prev => {
      const newFolders = [...prev];
      newFolders.sort((a, b) => {
        const indexA = order.indexOf(a.id);
        const indexB = order.indexOf(b.id);
        return indexA - indexB;
      });
      return newFolders;
    });

    try {
      await client.reorderFolders(order);
      // No reload needed, state is already correct
    } catch (e) {
      console.error(e);
      // Error toast removed - silent operation
      // Reload on error to sync real state
      await loadFolders();
    }
  }, [client, loadFolders]);

  const handleReorderLinks = useCallback(async (slugs: string[]) => {
    // Optimistic update
    setLinks(prev => prev.map(l => {
      const idx = slugs.indexOf(l.slug);
      return idx !== -1 ? { ...l, position: idx } : l;
    }));

    try {
      await client.reorderLinks(slugs);
      // Removed loadLinks to avoid refresh
    } catch (e) {
      console.error(e);
      // Error toast removed - silent operation
      // Reload on error to restore state
      await loadLinks(debouncedSearch);
    }
  }, [client, loadLinks, debouncedSearch]);

  const handleUpdateLinkPosition = useCallback(async (slug: string, position: { beforeSlug?: string | null; afterSlug?: string | null }) => {
    console.log('[App] handleUpdateLinkPosition called:', slug, position);
    try {
      await client.updateLinkPosition(slug, position);
      console.log('[App] updateLinkPosition API call succeeded');
      // Reload to get correct order from server
      await loadLinks(debouncedSearch);
    } catch (e) {
      console.error('Position update failed:', e);
      await loadLinks(debouncedSearch);
    }
  }, [client, loadLinks, debouncedSearch]);

  const handleDelete = async (slug: string) => {
    if (!user) return;
    // Direct delete without confirmation as requested
    setDeletingSlug(slug);
    try {
      await client.remove(slug);
      // Toast removed as requested - silent deletion
      await loadLinks(debouncedSearch);
      const profile = await client.me();
      setUser(profile);
    } catch (error) {
      // Error toast removed - silent operation
    } finally {
      setDeletingSlug(null);
    }
  };

  const editingValues: LinkPayload | undefined = editing
    ? {
      slug: editing.slug,
      destination: editing.destination,
      description: editing.description ?? '',
      folderId: editing.folderId,
    }
    : undefined;

  const handleLogout = () => {
    setAuthToken('');
    setUser(null);
    setLinks([]);
  };

  const handleUpgradeClick = async () => {
    if (!authToken) {
      return;
    }
    try {
      const data = await client.createCheckout();
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      // Error toast removed - silent operation
    }
  };

  const handleGoogleClick = async () => {
    try {
      const redirect = typeof window !== 'undefined' ? window.location.origin : '';
      if (!redirect) throw new Error('Unknown redirect');
      const data = await client.googleAuthUrl(redirect);
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      setAuthError(error instanceof Error ? error.message : 'Google sign-in failed');
    }
  };

  // Handle legal page navigation
  const handleLegalClick = (page: LegalPage) => {
    const basePath = window.location.pathname.replace(/\/legal\/.*$/, '');
    window.history.pushState({}, '', `${basePath}/legal/${page}`);
    setLegalPage(page);
    window.scrollTo(0, 0);
  };

  const handleLegalBack = () => {
    const basePath = window.location.pathname.replace(/\/legal\/.*$/, '');
    window.history.pushState({}, '', basePath || '/');
    setLegalPage(null);
  };

  // Check URL for legal page on mount and popstate
  useEffect(() => {
    const checkLegalPath = () => {
      const path = window.location.pathname;
      const legalMatch = path.match(/\/legal\/(mentions|privacy|cgv)$/);
      if (legalMatch) {
        setLegalPage(legalMatch[1] as LegalPage);
        window.scrollTo(0, 0);
      } else {
        setLegalPage(null);
      }
    };

    checkLegalPath();
    window.addEventListener('popstate', checkLegalPath);
    return () => window.removeEventListener('popstate', checkLegalPath);
  }, []);

  // Show legal page if selected
  if (legalPage) {
    return (
      <>
        <LegalPages
          page={legalPage}
          lang={lang}
          t={t}
          onBack={handleLegalBack}
          onChangeLang={setLang}
          onLegalClick={handleLegalClick}
        />
        <CookieBanner lang={lang} onOpenPrivacy={() => { setLegalPage('privacy'); window.scrollTo(0, 0); }} />
      </>
    );
  }

  if (!user) {
    const shortDomainLabel = shortDomain.replace(/^https?:\/\//, '');
    return (
      <>
        <Pricing
          lang={lang}
          onChangeLang={setLang}
          onGoogleClick={handleGoogleClick}
          authError={authError ?? undefined}
          t={t}
          priceLabel={proPriceLabel}
          domain={shortDomainLabel}
          assetBase={assetBase}
          onLegalClick={handleLegalClick}
        />
        <CookieBanner lang={lang} onOpenPrivacy={() => { setLegalPage('privacy'); window.scrollTo(0, 0); }} />
      </>
    );
  }

  const totalLinks = links.length;
  const latestUpdateText =
    totalLinks > 0 ? new Date(links[0].updatedAt).toLocaleString(locale) : '';
  const plan = user.plan ?? 'free';
  const linksUsed = typeof user.linksUsed === 'number' ? user.linksUsed : totalLinks;
  const linksLimit = typeof user.linksLimit !== 'undefined' ? user.linksLimit : plan === 'free' ? 5 : null;
  const limitReached = plan !== 'pro' && typeof linksLimit === 'number' && linksUsed >= linksLimit;
  const planLabel = plan === 'pro' ? t('plan.pro') : t('plan.free');

  // Close mobile form when a link is created or updated
  const handleMobileFormClose = () => setMobileFormOpen(false);
  const handleMobileCreate = (payload: LinkPayload) => {
    handleCreate(payload);
    handleMobileFormClose();
  };
  const handleMobileUpdate = (payload: LinkPayload) => {
    handleUpdate(payload);
    handleMobileFormClose();
  };

  const handleCreateFolderAndReturn = async (name: string) => {
    try {
      const folder = await client.createFolder(name);
      await loadFolders();
      // Success toast removed - silent operation
      return folder;
    } catch (e) {
      console.error(e);
      // Error toast removed - silent operation
      return null;
    }
  };

  return (
    <div className="app-shell" dir={dir} lang={locale}>

      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <img src="/logo-light.svg" alt="ShortURL" className="brand-logo brand-logo-light" />
            <img src="/logo-dark.svg" alt="ShortURL" className="brand-logo brand-logo-dark" />
          </div>
          <div className="header-actions hide-mobile">
            {/* Links count badge */}
            {plan !== 'pro' && typeof linksLimit === 'number' && (
              <span className="links-count-badge">
                {linksUsed}/{linksLimit} {t('stats.linksCount').toLowerCase()}
              </span>
            )}

            {/* Upgrade button */}
            {plan !== 'pro' && (
              <button className="upgrade-btn" type="button" onClick={handleUpgradeClick}>
                {t('plan.upgrade')}
              </button>
            )}

            {/* Profile Avatar with Dropdown */}
            <div className="profile-avatar-wrapper">
              <button
                className="profile-avatar-btn"
                onClick={() => setProfileOpen(!profileOpen)}
                aria-expanded={profileOpen}
              >
                <span className="profile-avatar-circle">
                  {user.email.charAt(0).toUpperCase()}
                </span>
              </button>

              {profileOpen && (
                <div className="profile-dropdown">
                  {/* Email */}
                  <div className="profile-dropdown-email">
                    {user.email}
                  </div>

                  <div className="profile-dropdown-divider" />

                  {/* Theme Toggle */}
                  <div className="profile-dropdown-section">
                    <span className="profile-dropdown-label">Theme</span>
                    <div className="theme-toggle-group">
                      <button
                        className={`theme-toggle-btn ${theme === 'light' ? 'active' : ''}`}
                        onClick={() => setTheme('light')}
                      >
                        ☀️
                      </button>
                      <button
                        className={`theme-toggle-btn ${theme === 'system' ? 'active' : ''}`}
                        onClick={() => setTheme('system')}
                      >
                        💻
                      </button>
                      <button
                        className={`theme-toggle-btn ${theme === 'dark' ? 'active' : ''}`}
                        onClick={() => setTheme('dark')}
                      >
                        🌙
                      </button>
                    </div>
                  </div>

                  <div className="profile-dropdown-divider" />

                  {/* Language Picker */}
                  <div className="profile-dropdown-section">
                    <span className="profile-dropdown-label">Language</span>
                    <LanguagePicker value={lang} onChange={setLang} compact />
                  </div>

                  <div className="profile-dropdown-divider" />

                  {/* Logout */}
                  <button
                    className="profile-dropdown-logout"
                    onClick={() => { handleLogout(); setProfileOpen(false); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    {t('action.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="mobile-menu-controls show-mobile">
            <button
              className="ghost-button icon-button mobile-menu-toggle"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menu"
            >
              {mobileMenuOpen ? '✕' : '☰'}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="mobile-menu-dropdown">
              <div className="mobile-menu-section mobile-menu-header-row">
                <div className="mobile-menu-user-compact">
                  <span className="mobile-menu-email">{user.email}</span>
                  <span className="mobile-menu-plan" data-plan={plan}>
                    {planLabel} {plan !== 'pro' ? `· ${linksUsed}/${linksLimit}` : ''}
                  </span>
                </div>
                <LanguagePicker value={lang} onChange={setLang} compact />
              </div>

              {plan !== 'pro' && (
                <div className="mobile-menu-section mobile-menu-upgrade-section">
                  <button className="mobile-upgrade-btn" type="button" onClick={() => { handleUpgradeClick(); setMobileMenuOpen(false); }}>
                    {t('plan.upgrade')}
                  </button>
                </div>
              )}

              <div className="mobile-menu-section mobile-menu-footer">
                <button className="mobile-logout-link" type="button" onClick={handleLogout}>
                  {t('action.logout')}
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="two-column">
        {/* Mobile Form Backdrop */}
        {mobileFormOpen && <div className="mobile-form-backdrop show-mobile" onClick={() => setMobileFormOpen(false)} />}

        <div className={`right-panel ${mobileFormOpen ? 'mobile-open' : ''}`}>
          {/* Close button for mobile modal */}
          {mobileFormOpen && (
            <button className="mobile-form-close show-mobile" onClick={() => setMobileFormOpen(false)}>✕</button>
          )}
          <LinkForm
            mode={editing ? 'edit' : 'create'}
            onSubmit={editing ? handleMobileUpdate : handleMobileCreate}
            onCancel={() => { setEditing(null); setMobileFormOpen(false); }}
            initialValues={editing ? editingValues : undefined}
            submitting={editing ? editSubmitting : submitting}
            shortDomain={shortDomain}
            disabled={!user || (!editing && limitReached)}
            notice={
              !editing && limitReached
                ? {
                  title: t('plan.limitReachedTitle'),
                  body: t('plan.limitReachedBody'),
                  actionLabel: t('plan.upgradeCta'),
                  onAction: handleUpgradeClick,
                }
                : undefined
            }
            t={(key) => t(key)}
            folders={folders}
            isPro={plan === 'pro'}
            onCreateFolder={handleCreateFolderAndReturn}
          />
        </div>

        <div className="left-panel">
          <div className="card settings-panel stats-panel">
            <div className="stat-row">
              <div className="stat-item">
                <p className="label">{t('stats.linksCount')}</p>
                <strong>{totalLinks}</strong>
              </div>
              <div className="stat-item">
                <p className="label">{t('stats.lastUpdated')}</p>
                <strong>{latestUpdateText || '—'}</strong>
              </div>
            </div>
          </div>

          {/* Quick Shorten - inline input with expandable options */}
          <div className={`quick-shorten show-mobile ${quickUrl.trim() ? 'expanded' : ''}`}>
            <div className="quick-shorten-main">
              <svg className="quick-shorten-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              <input
                type="url"
                className="quick-shorten-input"
                placeholder={t('quickShorten.placeholder')}
                value={quickUrl}
                onChange={(e) => setQuickUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && quickUrl.trim() && handleQuickShorten()}
                disabled={quickSubmitting}
              />
            </div>

            {/* Expandable options - show when URL is entered */}
            {quickUrl.trim() && (
              <div className="quick-shorten-options">
                {/* Alias row */}
                <div className="quick-option-row">
                  <span className="quick-option-prefix">shorturl.bitek.fr/</span>
                  <input
                    type="text"
                    className="quick-option-input"
                    placeholder={t('linkForm.slugPlaceholder')}
                    value={quickAlias}
                    onChange={(e) => setQuickAlias(e.target.value)}
                    disabled={quickSubmitting}
                  />
                </div>

                {/* Description row */}
                <div className="quick-option-row">
                  <span className="quick-option-prefix">{t('quickShorten.descLabel')}</span>
                  <input
                    type="text"
                    className="quick-option-input"
                    placeholder={t('linkForm.descriptionPlaceholder')}
                    value={quickNote}
                    onChange={(e) => setQuickNote(e.target.value)}
                    disabled={quickSubmitting}
                  />
                </div>

                {/* Code/Password row */}
                <div className="quick-option-row">
                  <span className="quick-option-prefix">{t('quickShorten.codeLabel')}</span>
                  <input
                    type="text"
                    className="quick-option-input"
                    placeholder={t('quickShorten.codePlaceholder')}
                    value={quickCode}
                    onChange={(e) => setQuickCode(e.target.value)}
                    disabled={quickSubmitting}
                  />
                </div>

                {/* Submit button */}
                <button
                  className="quick-shorten-submit"
                  onClick={handleQuickShorten}
                  disabled={quickSubmitting || !quickUrl.trim()}
                >
                  {quickSubmitting ? (
                    <span className="spinner-small" />
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                      </svg>
                      {t('quickShorten.submit')}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <div className="search-container">
            <div className="field-group" style={{ position: 'relative' }}>
              <label className="hide-mobile">{t('search.label')}</label>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="search-icon"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                placeholder={t('search.placeholder')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="search-input has-icon"
              />
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">{t('loading.links')}</div>
      ) : (
        <LinkTable
          links={links}
          shortDomain={shortDomain}
          deletingSlug={deletingSlug}
          onEdit={(link) => { setEditing(link); setMobileFormOpen(true); }}
          onDelete={handleDelete}
          onRefresh={() => loadLinks(debouncedSearch)}
          onCreateFolder={() => setCreateFolderOpen(true)}
          onDeleteFolder={handleDeleteFolder}
          onEditFolder={handleEditFolder}
          onReorderFolders={handleReorderFolders}
          onReorderLinks={handleReorderLinks}
          onUpdateLinkPosition={handleUpdateLinkPosition}
          folders={folders}
          onMoveLink={setMovingLink}
          onUpdateLinkFolder={handleUpdateLinkFolder}
          t={t}
          locale={locale}
          authToken={authToken}
          search={search}
          onSearchChange={setSearch}
        />
      )}

      {/* Modals */}
      {createFolderOpen && (
        <CreateFolderModal
          onClose={() => setCreateFolderOpen(false)}
          onSubmit={async (name) => {
            try {
              await client.createFolder(name);
              await loadFolders();
            } catch (e) {
              setToast({ type: 'error', text: 'Erreur création dossier' });
              throw e; // Rethrow to let modal know it failed
            }
          }}
        />
      )}

      {movingLink && (
        <MoveToFolderModal
          link={movingLink}
          folders={folders}
          onClose={() => setMovingLink(null)}
          onMove={handleMoveLink}
        />
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.text}</div>}

      <Footer t={t} onLegalClick={handleLegalClick} />
      <CookieBanner lang={lang} onOpenPrivacy={() => { setLegalPage('privacy'); window.scrollTo(0, 0); }} />
    </div>
  );
};

export default App;
